import { createAsyncThunk } from '@reduxjs/toolkit';
import { captureException } from '@sentry/browser';
import axios from 'axios';
import instance from '../../axiosInstance';
import Search from '../../interfaces/search';
import Invitation, {
  InvitationPayload,
  InvitationCancellationPayload,
  InvitationCreationError,
} from '../../interfaces/invitation';
import { getSearchFilters } from '../searchFilters/actions';
import {
  GENERIC_ERROR_MESSAGE,
  extractErrorMessage,
} from '../../utils/errors/extractErrorMessage';

export interface SearchPropsPayload {
  [key: string]: any;
}

/// /
// Error Handling
/// /
const FALLBACK_MESSAGES: { [status: number]: string } = {
  404: 'The search you are looking for does not exist.',
  500: 'There was an error with the server. Please try again.',
};

const handleSearchErrors = (err: any) => {
  captureException(err);

  const status: number | undefined = err?.response?.status;
  const statusFallback = status ? FALLBACK_MESSAGES[status] : undefined;
  const message =
    statusFallback ??
    extractErrorMessage(err?.response?.data) ??
    GENERIC_ERROR_MESSAGE;

  return { error: true, errorData: { message } };
};

/// /
// Async Thunk Actions
/// /
export const fetchSearchById = createAsyncThunk<Search, string>(
  'searches/fetchSearchById',
  async (searchId, { rejectWithValue, dispatch, signal }) => {
    const safeSearchId = encodeURIComponent(searchId);
    try {
      const response = await instance.get<Search>(
        `/deals/search/${safeSearchId}/?format=json`,
        { signal },
      );
      const search = response.data;
      await dispatch(getSearchFilters({ searchId: search.id, signal }));
      try {
        const defaultPlaybookResponse = await instance.get(
          `/deals/search/${encodeURIComponent(String(search.id))}/default-playbook/`,
          { signal },
        );
        search.default_playbook = defaultPlaybookResponse.data || null;
        return search;
      } catch (e) {
        if (axios.isCancel(e)) {
          throw e;
        }
        search.default_playbook = undefined;
        return search;
      }
    } catch (err) {
      if (axios.isCancel(err)) {
        return rejectWithValue({ aborted: true });
      }
      return rejectWithValue(handleSearchErrors(err));
    }
  },
);

export const patchSearchById = createAsyncThunk<
  Search | any,
  [number | string, SearchPropsPayload]
>('searches/patchSearchById', async (tuple, { rejectWithValue }) => {
  try {
    const searchId = tuple[0];
    const props = tuple[1];
    const response = await instance.patch(
      `/deals/search/${searchId}/?format=json`,
      { ...props },
    );
    return response.data;
  } catch (err) {
    return rejectWithValue(handleSearchErrors(err));
  }
});

export const endSearchActivity = createAsyncThunk<any, string | number>(
  'searches/endSearchActivity',
  async (activityId) => {
    const response = await instance.get(
      `/deals/search-activity/${activityId}/?end=true`,
    );
    if (response.status === 200) {
      return response.data;
    }
    return {
      hasError: true,
      errorMessage: 'There was an error ending the search',
    };
  },
);

export const patchDealById = createAsyncThunk<any, any>(
  'searches/patchDealById',
  async (patchParams) => {
    try {
      const response = await instance.patch(`/deals/${patchParams.id}/`, {
        ...patchParams.data,
      });
      return response.data;
    } catch (err) {
      const typedErr: any = err;
      return { error: true, data: typedErr.response.data };
    }
  },
);

export const postInvitation = createAsyncThunk<
  Invitation | any,
  InvitationPayload
>(
  'searches/postInvitation',
  async (properties: InvitationPayload, thunkApi) => {
    try {
      const response = await instance.post(`/deals/invitation/`, {
        ...properties,
      });
      return response.data;
    } catch (err) {
      const typedErr: any = err;
      const invitationError: InvitationCreationError = {
        message:
          extractErrorMessage(typedErr?.response?.data) ??
          GENERIC_ERROR_MESSAGE,
        agentId: properties.agent,
      };
      return thunkApi.rejectWithValue(invitationError);
    }
  },
);

export const cancelInvitationById = createAsyncThunk<
  any,
  InvitationCancellationPayload
>('searches/cancelInvitation', async (cancellationPayload) => {
  const { invitationId, agentId } = cancellationPayload;
  const response = await instance.get(
    `/deals/invitation/${invitationId}/cancel/`,
  );
  const dataWithIds = { ...response.data, invitationId, agentId };
  return dataWithIds;
});
